import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useRecoveryFlow } from 'src/auth/hooks/useRecoveryFlow';
import { RecoveryEmailForm } from 'src/auth/recovery/RecoveryEmailForm';
import { RecoveryCodeForm } from 'src/auth/recovery/RecoveryCodeForm';
import { ory } from 'src/auth/ory';
import { Logger } from 'src/utils/Logger';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import PageLoader from 'src/UI/loader/PageLoader';

const AUTH_METHOD_LINK_RECOVERY = 'link_recovery';

export const RecoveryPage = () => {
  const [routeReady, setRouteReady] = useState(false);
  const isRedirectingRef = useRef(false);
  const [searchParams] = useSearchParams();
  const flowIdParam = searchParams.get('flow');

  useEffect(() => {
    let cancelled = false;

    const checkSessionAndFlow = async () => {
      if (isRedirectingRef.current) return;

      try {
        const { data: session } = await ory.toSession();
        const authMethods = session.authentication_methods ?? [];
        const mostRecentMethod = authMethods[authMethods.length - 1]?.method;

        if (mostRecentMethod === AUTH_METHOD_LINK_RECOVERY) {
          if (isRedirectingRef.current) return;
          isRedirectingRef.current = true;
          const { data: settingsFlow } = await ory.createBrowserSettingsFlow();
          window.location.replace(`/invite?flow=${settingsFlow.id}`);
          return;
        }
      } catch {
        Logger.info('No active session, proceeding with recovery flow');
      }

      if (cancelled || isRedirectingRef.current) return;

      if (flowIdParam) {
        try {
          const { data: flow } = await ory.getRecoveryFlow({ id: flowIdParam });
          if (cancelled || isRedirectingRef.current) return;
          if (flow.active === 'link') {
            isRedirectingRef.current = true;
            window.location.replace('/invite?expired=true');
            return;
          }
        } catch {
          Logger.info('Failed to fetch recovery flow, proceeding with normal flow');
        }
      }

      if (cancelled || isRedirectingRef.current) return;
      setRouteReady(true);
    };

    checkSessionAndFlow();

    return () => {
      cancelled = true;
    };
  }, [flowIdParam]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    isLoading,
    error,
    step,
    submittedEmail,
    defaultEmail,
    submitEmail,
    submitCode,
    goBackToEmail,
  } = useRecoveryFlow();

  const handleEmailSubmit = async (email: string) => {
    setIsSubmitting(true);
    await submitEmail(email);
    setIsSubmitting(false);
  };

  const handleCodeSubmit = async (code: string) => {
    setIsSubmitting(true);
    const { success } = await submitCode(code);
    setIsSubmitting(false);

    if (success) {
      try {
        const { data: settingsFlow } = await ory.createBrowserSettingsFlow();
        window.location.href = `/settings?flow=${settingsFlow.id}&source=recovery`;
      } catch (error) {
        Logger.error('Failed to create settings flow after recovery', error);
        window.location.href = '/';
      }
    }
  };

  if (!routeReady) {
    return <PageLoader />;
  }

  return (
    <AuthPageLayout isLoading={isLoading}>
      {step === 'email' ? (
        <RecoveryEmailForm
          defaultEmail={defaultEmail}
          error={error}
          isSubmitting={isSubmitting}
          onSubmit={handleEmailSubmit}
        />
      ) : (
        <RecoveryCodeForm
          email={submittedEmail}
          error={error}
          isSubmitting={isSubmitting}
          onSubmit={handleCodeSubmit}
          onBack={goBackToEmail}
        />
      )}
    </AuthPageLayout>
  );
};
